import { useState, useCallback } from 'react';
import MintelTracking from 'mintel-user-tracking';

/**
 * A function to be passed to apps to initialize mixpanel
 * @callback startTracking
 * @param {string} currentProduct The current product
 * @param {boolean} production Whether to use the production mixpanel project
 * @param {Object} options Configuration options
 * @param {number} options.recordSessionsPercent The percentage of sessions to record
 * @returns {Object} The mintel-user-tracking object
 */

/**
 * Returns a function to initialize mixpanel through mintel-user-tracking using
 * the current portal JWT and account manager. The returned function takes the
 * current product name and if production as arguments. It will block mixpanel
 * being initialized twice on the same product.
 * @param {string} accessToken Portal access token
 * @param {string} oidcAccessToken OIDC access token
 * @returns {startTracking | false} The function to start tracking or false
 */
// eslint-disable-next-line import-x/no-anonymous-default-export
export default (
  accessToken: string | undefined,
  oidcAccessToken: string,
): typeof MintelTracking | false => {
  const [initializedProduct, setInitializedProduct] = useState('');

  const startTracking = useCallback(
    (
      currentProduct: string,
      production = false,
      options: { recordSessionsPercent?: number } = {},
    ) => {
      const { recordSessionsPercent = 0 } = options;
      if (initializedProduct !== currentProduct) {
        MintelTracking.handleMixpanelInit(
          accessToken,
          currentProduct,
          // Account manager no longer required, but positional arg is kept for compatibility
          '',
          production,
          recordSessionsPercent,
        );
        setInitializedProduct(currentProduct);
      }
      return MintelTracking;
    },
    [accessToken, initializedProduct, setInitializedProduct],
  );

  // If there is an oidcAccessToken but no accessToken, wait
  // for them to load, otherwise this is a public user which we want to track.
  if (oidcAccessToken && !accessToken) {
    return false;
  }

  return startTracking;
};
